import * as React from 'react';

import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';

import { FaOptionIcon, chevronDownIcon } from '@europrocurement/l2d-icons';
import RejectionCodesChips from './RejectionCodesChips';

interface RejectionCode {
    id: number;
    code: string;
    libelle: string;
}

interface InvoiceEligibilityDetailsProps {
    eligibleCodes: RejectionCode[];
    ineligibleCodes: RejectionCode[];
    compact?: boolean;
}

/**
 * Composant accordéon pour afficher les détails d'éligibilité d'une facture
 *
 * Affiche un accordéon dépliable contenant la liste détaillée des codes de rejet
 * éligibles et non-éligibles d'une facture. Supporte un mode compact pour
 * l'affichage dans les cellules de tableau.
 *
 * @param eligibleCodes - Liste des codes de rejet éligibles
 * @param ineligibleCodes - Liste des codes de rejet non-éligibles
 * @param compact - Mode compact pour affichage dans tableau (réduit les tailles/espacements)
 */
const InvoiceEligibilityDetails: React.FunctionComponent<InvoiceEligibilityDetailsProps> =
    function ({ eligibleCodes, ineligibleCodes, compact = false }) {
        return (
            <Accordion
                elevation={0}
                disableGutters
                sx={{
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                        minHeight: compact ? 28 : 48,
                        maxHeight: compact ? 28 : 48,
                    },
                    '& .MuiAccordionDetails-root': {
                        px: compact ? 0.5 : 2,
                        py: compact ? 0.5 : 1,
                    },
                }}
            >
                <AccordionSummary
                    expandIcon={<FaOptionIcon {...chevronDownIcon.props} />}
                    sx={{
                        minHeight: 'unset',
                        '& .MuiAccordionSummary-content': {
                            margin: compact ? '4px 0' : '8px 0',
                            minHeight: 'unset',
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                            transform: compact ? 'scale(0.8)' : 'scale(1)',
                        },
                    }}
                >
                    <Typography
                        variant={compact ? 'caption' : 'body2'}
                        color="text.secondary"
                        sx={{ fontSize: compact ? '0.7rem' : undefined }}
                    >
                        {compact ? 'Codes' : 'Détail des codes de rejet'}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <RejectionCodesChips
                        codes={eligibleCodes}
                        type="eligible"
                        title="Éligibles"
                    />
                    <RejectionCodesChips
                        codes={ineligibleCodes}
                        type="ineligible"
                        title="Non-éligibles"
                    />
                </AccordionDetails>
            </Accordion>
        );
    };

export default InvoiceEligibilityDetails;
